import classNames from 'classnames';
import { useLayoutEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { BasketItem, ItemGroupTotal } from '../../../../types/basket';
import { config, getAssetUrl } from '../../../../utils/config';
import Collapse from '../../../atoms/Collapse';
import Price from '../../../atoms/Price';
import { getDiscounts } from '../ItemTotals';
import './style.scss';

type ProductRemoveProps = {
  isLoading: string;
  onRemoveItem: (id: string) => Promise<void>;
};

const SingleProduct = ({ item, isLoading, onRemoveItem }: { item: BasketItem } & ProductRemoveProps) => {
  const { total, ...discountComponents } = item.discount;
  const { showNetPrices, replaceFreeItemPriceWithText } = config.basket;

  const discounts = getDiscounts(discountComponents);
  const isDiscounted = !!discounts.length;
  const isRequired = item.itemGroup?.isRequired;
  const totalWithDiscounts = showNetPrices ? item.total.costWithoutTaxWithDiscount : item.total.costWithTaxWithDiscount;
  const totalWithoutDiscounts =
    showNetPrices ? item.total.costWithoutTaxWithoutDiscount : item.total.costWithTaxWithoutDiscount;

  return (
    <div className="product-group__option" key={item.id}>
      <div className="product-group__quantity">
        <span data-test-id="price-productgroup-item-quantity">{item.quantity}</span>
        <img className="product-group__quantity-icon" src={getAssetUrl('common/remove.svg')} />
      </div>
      <div className="product-group__info">
        <div className="product-group__name">{item.name}</div>
      </div>
      <div className="product-group__price">
        {isDiscounted && (
          <div className="product-group__discounts">
            <Price
              price={totalWithoutDiscounts}
              isInactive
              className="product-group__discount"
              dataTestId={'price-productgroup-without-discount'}
            />
          </div>
        )}
        {replaceFreeItemPriceWithText && !totalWithDiscounts ?
          <FormattedMessage id="basket.item.freeItemText" />
        : <Price
            price={totalWithDiscounts}
            isDiscounted={isDiscounted}
            dataTestId="price-productgroup-singleitem-total"
          />
        }
        {item.quantity > 1 && (
          <span
            className="product-group__price-for-multiple-items"
            data-test-id="price-productgroup-price-for-multiple"
          >
            <FormattedMessage
              id="basket.item.group.priceForItems"
              values={{
                items: item.quantity
              }}
            />
          </span>
        )}
      </div>
      <button
        className="product-group__remove-button"
        type="button"
        onClick={() => onRemoveItem(item.id)}
        disabled={isRequired || !!isLoading}
        data-test-id="item-remove"
      >
        <img
          className={classNames('product-group__remove-icon', { 'cursor-disabled': isRequired })}
          src={getAssetUrl(`common/remove${isRequired ? 'Inactive' : ''}.svg`)}
        />
      </button>
    </div>
  );
};

type ProductGroupProps = { subItems: BasketItem[]; total: ItemGroupTotal } & ProductRemoveProps;

const ProductGroup = ({ subItems, isLoading, total, onRemoveItem }: ProductGroupProps) => {
  const [isOpen, setOpen] = useState(true);

  useLayoutEffect(() => {
    setOpen(false);
  }, []);

  return (
    <div className="product-group">
      <div className="product-group__options">
        <div className="product-group__control" onClick={() => setOpen(!isOpen)}>
          <div className="product-group__trigger">
            <FormattedMessage id="basket.item.group.options" />
            <img
              className="product-group__chevron-icon"
              src={getAssetUrl('inputs/chevronDown.svg')}
              style={{ transform: `rotate(${isOpen ? '180deg' : '0'})` }}
            />
          </div>
          <Price price={total.bruttoWithoutMainProduct} dataTestId="price-productgroup-totalwithoutmainproduct" />
        </div>
        <Collapse isOpen={isOpen} dep={subItems}>
          <div className="product-group__menu">
            {subItems.map(item => (
              <SingleProduct item={item} key={item.id} isLoading={isLoading} onRemoveItem={onRemoveItem} />
            ))}
          </div>
        </Collapse>
      </div>
      <div className="product-group__total">
        <FormattedMessage id="basket.item.group.total" />
        <Price price={total.brutto} dataTestId="price-productgroup-total" />
      </div>
    </div>
  );
};
export default ProductGroup;
